import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { toast } from "react-toastify";
import { BrandingContext } from "../../BrandingProvider";
import { usePermissions } from "../login/PermissionContext";
import { displayPatientName } from "../../utils/displayPatientName";
import HlthtekFooterLogo from "../../assets/images/logo/NavLogo_new.png";
import {
  ESTIMATE_PRINT_NO_LETTERHEAD_MARGIN_MM,
  applyWithoutLetterheadLayoutOverrides,
  alignEstimateLineItemsCell,
  composeEstimateWithLetterheadTemplate,
  downloadPdfBytes,
  drawEstimatePdfFooter,
  getAmountInWordsLayout,
  getEstimatePdfJsFormat,
  getEstimatePdfPageLayout,
  getNoLetterheadMarginsMm,
  measureEstimatePdfFooter,
  normalizeEstimatePageSizeKey,
  normalizeEstimatePrintType,
  normalizeLetterheadTemplateType,
  numberToWords,
  openPdfBytesInNewTab,
  parseEstimateMarginsByPageSize,
} from "./estimatePdfUtils";

// Same document engine as ViewEstimate.js (real jsPDF + autoTable + letterhead overlay via
// pdf-lib, A4/A5, facility branding) — this is the screen shown right after an estimate is
// created, before its estimate_id is even known to the caller, so it works off the
// estimateData the create call returned rather than re-fetching by id.

const EstimatePreview = () => {
  const location = useLocation();
  const history = useHistory();
  const brandingData = useContext(BrandingContext);
  const { userInfo } = usePermissions();

  const { estimateData } = location.state || {};

  const [fetchedPatient, setFetchedPatient] = useState(null);
  const [estimatePaperSize, setEstimatePaperSize] = useState("a4");
  const [estimatePrintType, setEstimatePrintType] = useState("WITHOUT_LETTERHEAD");
  const [noLetterheadMarginsByPage, setNoLetterheadMarginsByPage] = useState(() => ({
    ...ESTIMATE_PRINT_NO_LETTERHEAD_MARGIN_MM,
  }));
  const [letterheadTemplate, setLetterheadTemplate] = useState(null);
  const [letterheadTemplateType, setLetterheadTemplateType] = useState("jpg");

  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL || "";
  const authToken = sessionStorage.getItem("accessToken") ?? "";

  useEffect(() => {
    if (
      sessionStorage.getItem("accessToken") == undefined &&
      sessionStorage.getItem("accessToken") == null
    ) {
      history.push("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================================================
      PATIENT DETAILS (age/gender/address/contact) — same lookup ViewEstimate.js uses
  ========================================================= */
  useEffect(() => {
    const patientId = estimateData?.patientId;
    if (!patientId || !authToken || !REACT_APP_BACKEND_ROUTE_URL) return;
    let cancelled = false;
    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_filtered_patient_data/`,
        {
          filter_type: "patient_id",
          filter_details: { patient_id: patientId },
          multipleResults: true,
        },
        { headers: { Authorization: `Bearer ${authToken}` } },
      )
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.status === "Success" && Array.isArray(res?.data?.patient_details)) {
          const first = res.data.patient_details[0];
          if (first) setFetchedPatient(first);
        }
      })
      .catch((e) => console.error("EstimatePreview: fetch patient failed", e));
    return () => {
      cancelled = true;
    };
  }, [estimateData?.patientId, authToken, REACT_APP_BACKEND_ROUTE_URL]);

  /* =========================================================
      PDF CONFIG — reuses the facility's invoice letterhead/page-size settings.
      Read-only GET; this is facility-level config, not invoice-instance data.
  ========================================================= */
  useEffect(() => {
    if (!authToken || !REACT_APP_BACKEND_ROUTE_URL) return;
    let cancelled = false;
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_invoice_config/`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (cancelled) return;
        if (res?.data?.status !== "Success") return;
        const cfg = res.data.invoice_template_config || {};
        const parsedMargins = parseEstimateMarginsByPageSize(cfg.margins);
        if (Object.keys(parsedMargins).length > 0) {
          setNoLetterheadMarginsByPage({
            ...ESTIMATE_PRINT_NO_LETTERHEAD_MARGIN_MM,
            ...parsedMargins,
          });
        }
        if (cfg.defaultPageSize) {
          setEstimatePaperSize(normalizeEstimatePageSizeKey(cfg.defaultPageSize));
        }
        if (cfg.defaultPrintType) {
          setEstimatePrintType(normalizeEstimatePrintType(cfg.defaultPrintType));
        }
        const templateRaw = cfg.letterheadTemplate;
        if (templateRaw != null && String(templateRaw).trim() !== "") {
          setLetterheadTemplate(String(templateRaw).trim());
          setLetterheadTemplateType(normalizeLetterheadTemplateType(cfg.letterheadTemplateType) || "jpg");
        } else {
          setLetterheadTemplate(null);
        }
      })
      .catch((e) => console.error("EstimatePreview: get_invoice_config failed", e));
    return () => {
      cancelled = true;
    };
  }, [authToken, REACT_APP_BACKEND_ROUTE_URL]);

  /* =========================================================
      BRANDING
  ========================================================= */
  const collateralImageItem = brandingData?.find((item) => item.name === "collateral_image");
  const collateralImage = collateralImageItem?.content || null;

  /* =========================================================
      HELPERS (same shape as ViewEstimate.js's)
  ========================================================= */
  const safeNumber = (val) => {
    const n = Number(val);
    return isNaN(n) ? 0 : n;
  };
  const formatMoney = (val) => safeNumber(val).toFixed(2);
  const formatDate = (raw) => {
    if (!raw || raw === "N/A") return "N/A";
    try {
      const d = raw instanceof Date ? raw : new Date(raw);
      if (isNaN(d.getTime())) return String(raw);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return String(raw);
    }
  };
  const buildFacilityAddress = (info) => {
    const parts = [
      info?.facility_address_line_1,
      info?.facility_address_line_2,
      info?.facility_address_line_3,
      info?.city,
      info?.state,
      info?.pincode ? `PIN: ${info.pincode}` : null,
    ];
    return parts.filter((x) => x && String(x).trim() !== "").join(", ");
  };
  const formatAgeGenderLine = (p) => {
    const age = p?.age ?? p?.age_years;
    const genderRaw = p?.gender ?? p?.sex ?? "N/A";
    const genderStr =
      genderRaw != null && String(genderRaw).trim() !== "" ? String(genderRaw).trim() : "N/A";
    const ageStr =
      age != null && String(age).trim() !== "" && String(age).toLowerCase() !== "n/a"
        ? `${age} Years`
        : "N/A";
    return `${ageStr} / ${genderStr}`;
  };
  const formatContactNumbers = (p) => {
    const primary = p?.phone_no || p?.phone || p?.contact_no || p?.mobile_no || "";
    const pri = primary != null ? String(primary).trim() : "";
    return pri || "N/A";
  };
  const formatAddress = (p) =>
    p?.address ||
    [p?.address_line, p?.locality, p?.city || p?.district, p?.state, p?.pincode]
      .filter((v) => v && !["null", "undefined", "n/a"].includes(String(v).toLowerCase()))
      .join(", ") ||
    "N/A";
  // Facility display name, preferring the friendly session name over any raw code
  const centerName = () =>
    sessionStorage.getItem("facility_display_name") || userInfo?.facility_name || "N/A";

  /* =========================================================
      LINE ITEM NORMALIZATION + TOTALS
      itemsList rows come from EstimateAdditon.js's in-progress form state:
      price = unit price, amount = net subtotal (post line discount), discount = line discount %.
  ========================================================= */
  const normalizedItems = (estimateData?.itemsList || []).map((item) => {
    const quantity = safeNumber(item.quantity);
    const unitPrice = safeNumber(item.price);
    const gross = unitPrice * quantity;
    const subtotal = safeNumber(item.amount);
    const discountPercent = safeNumber(item.discount);
    const discountAmount = Math.max(gross - subtotal, 0);
    return {
      item_name: item.itemName || "--",
      quantity,
      item_price: unitPrice,
      gross,
      discount_percent: discountPercent,
      discount_amount: discountAmount,
      subtotal,
    };
  });

  const grossItemsTotal = normalizedItems.reduce((sum, item) => sum + item.gross, 0);
  const netItemsTotal = normalizedItems.reduce((sum, item) => sum + item.subtotal, 0);
  const lineDiscountTotal = Math.max(grossItemsTotal - netItemsTotal, 0);
  const billDiscountPercent = safeNumber(estimateData?.discount);
  const billDiscountAmount = netItemsTotal * (billDiscountPercent / 100);
  const taxPercent = safeNumber(estimateData?.tax);
  const taxAmount = (netItemsTotal - billDiscountAmount) * (taxPercent / 100);
  const grandTotal = safeNumber(estimateData?.total);

  const summaryRows = [
    { label: `Items Total: Rs. ${formatMoney(grossItemsTotal)}`, bold: false },
    ...(lineDiscountTotal > 0.004
      ? [{ label: `Item Discount: Rs. ${formatMoney(lineDiscountTotal)}`, bold: false }]
      : []),
    { label: `Subtotal: Rs. ${formatMoney(netItemsTotal)}`, bold: false },
    ...(billDiscountPercent > 0
      ? [
          {
            label: `Bill Discount: Rs. ${formatMoney(billDiscountAmount)} (${formatMoney(billDiscountPercent)}%)`,
            bold: false,
          },
        ]
      : []),
    ...(taxPercent > 0
      ? [{ label: `Tax: Rs. ${formatMoney(taxAmount)} (${formatMoney(taxPercent)}%)`, bold: false }]
      : []),
    { label: `Total: Rs. ${formatMoney(grandTotal)}`, bold: true, big: true },
  ];

  /* =========================================================
      PDF BUILD — real vector jsPDF + autoTable document (not a screenshot).
      Returns the built jsPDF instance; caller decides letterhead compose + save/open.
  ========================================================= */
  const buildEstimatePdfDoc = (withLetterhead) => {
    const hasLetterheadTemplate = Boolean(letterheadTemplate && String(letterheadTemplate).trim());
    const useTemplateLetterhead = withLetterhead && hasLetterheadTemplate;
    const useDrawnLetterhead = withLetterhead && !hasLetterheadTemplate;

    const doc = new jsPDF("p", "mm", getEstimatePdfJsFormat(estimatePaperSize));
    let {
      PAGE_WIDTH,
      labelX,
      valueX,
      rightX,
      valueOffset,
      addrWrap,
      centerWrap,
      tableFont,
      tableHeadFont,
      headerTitleFont,
      sideMargin,
      narrow,
      amountWordsRightEdge,
      headerBandH,
      infoLineGap,
      contentStartY,
      titleGap,
      tableCellPadding,
      headerLogoMaxW,
      headerLogoMaxH,
      infoFontSize,
      titleFontSize,
    } = getEstimatePdfPageLayout(doc, estimatePaperSize);

    let tableLeftMargin = sideMargin;
    let tableRightMargin = sideMargin + 2;
    let noLetterheadTop = narrow ? 20 : 30;
    let noLetterheadBottom = narrow ? 8 : 22;
    if (!useDrawnLetterhead) {
      const nlMargins = getNoLetterheadMarginsMm(estimatePaperSize, noLetterheadMarginsByPage);
      const overridden = applyWithoutLetterheadLayoutOverrides(
        {
          PAGE_WIDTH,
          labelX,
          valueX,
          rightX,
          valueOffset,
          addrWrap,
          centerWrap,
          sideMargin,
          narrow,
        },
        nlMargins,
      );
      labelX = overridden.labelX;
      valueX = overridden.valueX;
      rightX = overridden.rightX;
      addrWrap = overridden.addrWrap;
      centerWrap = overridden.centerWrap;
      sideMargin = overridden.sideMargin;
      amountWordsRightEdge = overridden.amountWordsRightEdge;
      contentStartY = overridden.contentStartY;
      tableLeftMargin = overridden.tableLeftMargin;
      tableRightMargin = overridden.tableRightMargin;
      noLetterheadTop = overridden.noLetterheadTop;
      noLetterheadBottom = overridden.noLetterheadBottom;
    }

    const headerLogoDataUrl = collateralImage ? `data:image/png;base64,${collateralImage}` : null;
    const footerLayout = measureEstimatePdfFooter(doc, estimatePaperSize);

    function drawHeader() {
      doc.setFillColor(33, 205, 192);
      doc.rect(0, 0, PAGE_WIDTH, headerBandH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(headerTitleFont);
      doc.text(
        sessionStorage.getItem("facility_display_name") || "Super Galaxy Health",
        narrow ? 6 : 10,
        narrow ? 8 : 12,
      );
      doc.setTextColor(0);
      try {
        if (headerLogoDataUrl) {
          const imgProps = doc.getImageProperties(headerLogoDataUrl);
          const ratio = imgProps.width / imgProps.height;
          let width = headerLogoMaxW;
          let height = width / ratio;
          if (height > headerLogoMaxH) {
            height = headerLogoMaxH;
            width = height * ratio;
          }
          doc.addImage(
            headerLogoDataUrl,
            "PNG",
            PAGE_WIDTH - sideMargin - width,
            narrow ? 2 : 3,
            width,
            height,
          );
        }
      } catch (e) {
        // ignore — fall back to text-only header
      }
    }

    function drawFooterTemplate() {
      if (narrow) return;
      drawEstimatePdfFooter(doc, HlthtekFooterLogo, footerLayout);
    }

    const bottomMargin = useDrawnLetterhead
      ? narrow
        ? 8
        : Math.max(25, footerLayout.bandH + 8)
      : noLetterheadBottom;
    doc.autoTableSetDefaults({
      margin: {
        bottom: bottomMargin,
        ...(useDrawnLetterhead ? {} : { top: noLetterheadTop }),
      },
      didDrawPage: useDrawnLetterhead
        ? function () {
            drawHeader();
            drawFooterTemplate();
          }
        : function () {},
    });

    const PAGE_HEIGHT = doc.internal.pageSize.height;
    let y = contentStartY;
    if (useDrawnLetterhead) drawHeader();

    // TITLE
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    doc.text("ESTIMATE", labelX, y);
    y += titleGap;

    doc.setFontSize(infoFontSize);
    doc.setFont("helvetica", "normal");
    const patientY = y;
    const patientName = displayPatientName(estimateData?.patientName, "N/A");

    doc.setFont("helvetica", "bold");
    doc.text("Name:", labelX, patientY);
    doc.setFont("helvetica", "normal");
    doc.text(patientName, valueX, patientY);

    doc.setFont("helvetica", "bold");
    doc.text("Age/Gender:", labelX, patientY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(formatAgeGenderLine(fetchedPatient), valueX, patientY + infoLineGap);

    doc.setFont("helvetica", "bold");
    doc.text("Contact No:", labelX, patientY + infoLineGap * 2);
    doc.setFont("helvetica", "normal");
    doc.text(formatContactNumbers(fetchedPatient), valueX, patientY + infoLineGap * 2);

    doc.setFont("helvetica", "bold");
    doc.text("Address:", labelX, patientY + infoLineGap * 3);
    doc.setFont("helvetica", "normal");
    const addressLines = doc.splitTextToSize(formatAddress(fetchedPatient), addrWrap).slice(0, 2);
    addressLines.forEach((line, i) => {
      doc.text(line, valueX, patientY + infoLineGap * 3 + i * infoLineGap);
    });

    // RIGHT BLOCK
    const rightY = patientY;
    // "Center Address:" and "Valid Until:" run wider than the invoice engine's original
    // 5-line block (Bill No/Pat ID/Date/Center/Address) was tuned for — size the value
    // column to the widest label actually used here instead of the fixed valueOffset,
    // so long labels can't overlap the value text next to them.
    const rightLabels = ["Estimate No:", "Pat ID:", "Date:", "Valid Until:", "Center:", "Center Address:"];
    doc.setFont("helvetica", "bold");
    const rightGap = narrow ? 2 : 2.5;
    const rightValueOffset =
      Math.max(valueOffset, ...rightLabels.map((label) => doc.getTextWidth(label))) + rightGap;
    const rightValueX = rightX + rightValueOffset;
    const rightCenterWrap = Math.max(20, PAGE_WIDTH - sideMargin - rightValueX - 2);

    doc.text("Estimate No:", rightX, rightY);
    doc.setFont("helvetica", "normal");
    doc.text(estimateData?.estimateNumber || "N/A", rightValueX, rightY);

    doc.setFont("helvetica", "bold");
    doc.text("Pat ID:", rightX, rightY + infoLineGap);
    doc.setFont("helvetica", "normal");
    doc.text(estimateData?.patientId || "N/A", rightValueX, rightY + infoLineGap);

    doc.setFont("helvetica", "bold");
    doc.text("Date:", rightX, rightY + infoLineGap * 2);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(estimateData?.estimateDate), rightValueX, rightY + infoLineGap * 2);

    doc.setFont("helvetica", "bold");
    doc.text("Valid Until:", rightX, rightY + infoLineGap * 3);
    doc.setFont("helvetica", "normal");
    doc.text(formatDate(estimateData?.expiryDate), rightValueX, rightY + infoLineGap * 3);

    doc.setFont("helvetica", "bold");
    doc.text("Center:", rightX, rightY + infoLineGap * 4);
    doc.setFont("helvetica", "normal");
    doc.text(
      doc.splitTextToSize(centerName(), rightCenterWrap).slice(0, 1),
      rightValueX,
      rightY + infoLineGap * 4,
    );

    doc.setFont("helvetica", "bold");
    doc.text("Center Address:", rightX, rightY + infoLineGap * 5);
    doc.setFont("helvetica", "normal");
    const centerAddr = buildFacilityAddress(userInfo) || "N/A";
    doc.text(
      doc.splitTextToSize(centerAddr, rightCenterWrap).slice(0, 2),
      rightValueX,
      rightY + infoLineGap * 5,
    );

    y = Math.max(
      rightY + infoLineGap * 7,
      patientY + infoLineGap * 3 + addressLines.length * infoLineGap + infoLineGap,
    );

    // ITEM TABLE
    const tableBody = normalizedItems.map((item) => [
      item.item_name,
      item.quantity || 0,
      Number(item.item_price).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.gross).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.discount_percent).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.discount_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
      Number(item.subtotal).toLocaleString("en-IN", { minimumFractionDigits: 2 }),
    ]);

    doc.autoTable({
      startY: y,
      head: [["Service Description", "Qty", "Price (Rs.)", "Gross (Rs.)", "Discount %", "Discount (Rs.)", "Amount (Rs.)"]],
      body: tableBody,
      theme: "plain",
      styles: {
        font: "helvetica",
        fontSize: tableFont,
        cellPadding: tableCellPadding,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "center" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
      },
      didParseCell: alignEstimateLineItemsCell,
      headStyles: {
        fillColor: [199, 244, 239],
        textColor: 0,
        fontStyle: "bold",
        fontSize: tableHeadFont,
      },
      margin: { left: tableLeftMargin, right: tableRightMargin },
      pageBreak: "auto",
    });

    let currentY = doc.lastAutoTable.finalY + 5;
    if (currentY + 60 > PAGE_HEIGHT) {
      doc.addPage();
      currentY = useDrawnLetterhead ? 20 : noLetterheadTop;
    }

    // TOTALS (right-aligned)
    const RIGHT_X = PAGE_WIDTH - tableRightMargin;
    const summaryFontSize = narrow ? 5 : 8;
    const rowGap = narrow ? 2.8 : 5;
    doc.setFont("helvetica", "bold");
    summaryRows.forEach((row, i) => {
      doc.setFontSize(row.big ? summaryFontSize + 2 : summaryFontSize);
      if (row.big) doc.setTextColor(14, 63, 102);
      doc.text(row.label, RIGHT_X, currentY + rowGap * i, { align: "right" });
      if (row.big) doc.setTextColor(0);
    });
    const estimateNotes = String(estimateData?.notes ?? estimateData?.note ?? "").trim();
    const noteLines = estimateNotes
      ? doc.splitTextToSize(
          `Notes: ${estimateNotes}`,
          Math.max(28, (RIGHT_X - labelX) * 0.48),
        )
      : [];
    if (noteLines.length) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(narrow ? 5 : 8);
      doc.text(noteLines, labelX, currentY);
    }
    const noteLineHeight = (narrow ? 5 : 8) * 0.3528 * 1.15;
    const summaryEndY = Math.max(
      currentY + rowGap * (summaryRows.length - 1),
      noteLines.length ? currentY + noteLineHeight * noteLines.length : currentY,
    );

    // AMOUNT IN WORDS
    const amountText = `${numberToWords(grandTotal)} Rupees Only`;
    const prefixText = "Estimate Amount in Words: ";
    const thanksRightEdge = narrow ? Math.min(amountWordsRightEdge + 6, RIGHT_X - 4) : RIGHT_X;
    const { fs, textWidth, wrappedAmount, lineHeight } = getAmountInWordsLayout(
      doc,
      prefixText,
      amountText,
      thanksRightEdge,
      narrow,
    );
    const wordsBlockHeight = 6 + wrappedAmount.length * lineHeight;

    const gapAfterSummary = narrow ? 5 : 7;
    let wordsY = summaryEndY + gapAfterSummary;
    const footerReserve = narrow ? 6 : 22;
    if (wordsY + wordsBlockHeight + footerReserve > PAGE_HEIGHT) {
      doc.addPage();
      wordsY = useDrawnLetterhead ? (narrow ? 16 : 20) : noLetterheadTop;
    }

    doc.setFontSize(fs);
    doc.setFont("helvetica", "normal");
    doc.text(prefixText, labelX, wordsY);
    doc.setFont("helvetica", "bold");
    doc.text(wrappedAmount, labelX + textWidth, wordsY);
    const lastLineY = wordsY + (wrappedAmount.length - 1) * lineHeight;

    const noteY = lastLineY + (narrow ? 5.5 : 9);
    const signatureY = narrow ? wordsY : noteY;

    doc.setFontSize(narrow ? 5.5 : 8);
    doc.setFont("helvetica", "normal");
    doc.text("Note: This is a digitally generated estimate, not a tax invoice.", labelX, noteY);

    doc.setFontSize(narrow ? 6 : 8);
    doc.setFont("helvetica", "bold");
    doc.text("Authorised Signatory", RIGHT_X, signatureY, { align: "right" });

    return { doc, useTemplateLetterhead };
  };

  const emitPdf = async (withLetterhead, mode) => {
    if (!estimateData) return;
    const { doc, useTemplateLetterhead } = buildEstimatePdfDoc(withLetterhead);
    const filename = `Estimate-${estimateData?.estimateNumber || "EST"}.pdf`;

    if (useTemplateLetterhead) {
      try {
        const bytes = await composeEstimateWithLetterheadTemplate(
          doc,
          letterheadTemplate,
          letterheadTemplateType,
        );
        if (mode === "download") downloadPdfBytes(bytes, filename);
        else openPdfBytesInNewTab(bytes);
        return;
      } catch (e) {
        console.error("EstimatePreview: letterhead template compose failed", e);
        toast.error("Letterhead template failed; using plain layout instead.");
      }
    }
    if (mode === "download") doc.save(filename);
    else window.open(`${doc.output("bloburl")}#zoom=page-fit`, "_blank");
  };

  const handleDownload = () => emitPdf(estimatePrintType === "LETTERHEAD", "download");
  const handlePrint = () => emitPdf(estimatePrintType === "LETTERHEAD", "print");
  const handleHomeButton = () => history.push("/doctor/estimates");

  return (
    <>
      <Card style={{ marginBottom: "16px" }}>
        <Card style={{ width: "95%", height: "100%", padding: "16px", marginLeft: "40px", marginTop: "1%" }}>
          {estimateData ? (
            <>
              <Grid container style={{ padding: "50px", fontSize: "18px", lineHeight: "25px", paddingBottom: "20px" }}>
                {/* LEFT: patient */}
                <Grid item xs={7}>
                  <div style={{ display: "grid", rowGap: "2px", lineHeight: 1.35 }}>
                    <div style={{ fontWeight: 700 }}>Estimate</div>
                    <div>
                      <b>Name:</b> {displayPatientName(estimateData.patientName, "N/A")}
                    </div>
                    <div>
                      <b>Age/Gender:</b> {formatAgeGenderLine(fetchedPatient)}
                    </div>
                    <div>
                      <b>Contact No:</b> {formatContactNumbers(fetchedPatient)}
                    </div>
                    <div style={{ maxWidth: "420px", wordBreak: "break-word" }}>
                      <b>Address:</b> {formatAddress(fetchedPatient)}
                    </div>
                  </div>
                </Grid>

                {/* RIGHT: estimate meta */}
                <Grid item xs={5} style={{ textAlign: "left", paddingLeft: "230px" }}>
                  <div style={{ display: "grid", rowGap: "4px" }}>
                    <div>
                      <b>Estimate No:</b> {estimateData.estimateNumber || "N/A"}
                    </div>
                    <div>
                      <b>Pat ID:</b> {estimateData.patientId || "N/A"}
                    </div>
                    <div>
                      <b>Date:</b> {formatDate(estimateData.estimateDate)}
                    </div>
                    <div>
                      <b>Valid Until:</b> {formatDate(estimateData.expiryDate)}
                    </div>
                    <div>
                      <b>Center:</b> {centerName()}
                    </div>
                    <div style={{ maxWidth: "420px", wordBreak: "break-word" }}>
                      <b>Center Address:</b> {buildFacilityAddress(userInfo) || "N/A"}
                    </div>
                  </div>
                </Grid>
              </Grid>

              <Card style={{ height: "auto" }}>
                <Grid
                  container
                  style={{ backgroundColor: "#C7F4EF", padding: "16px 20px", alignItems: "center", borderRadius: "8px 8px 0 0" }}
                >
                  <Grid item xs={6}>
                    <Typography variant="h6" fontWeight="bold">
                      Estimate
                    </Typography>
                  </Grid>
                  <Grid item xs={6} textAlign="right">
                    {collateralImage && (
                      <img
                        src={`data:image/png;base64,${collateralImage}`}
                        height="70"
                        style={{ maxWidth: "220px", objectFit: "contain" }}
                        alt="logo"
                      />
                    )}
                  </Grid>
                </Grid>

                <Grid item xs={12} style={{ padding: "20px", display: "flex", justifyContent: "left", width: "100vw", fontSize: "16px", lineHeight: "22px" }}>
                  <div style={{ width: "95%" }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "2.4fr 0.7fr 1fr 1fr 1fr 1fr 1fr",
                        columnGap: 1,
                        fontWeight: "bold",
                        padding: "10px 0",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <Box>Service Description</Box>
                      <Box sx={{ textAlign: "center" }}>Qty</Box>
                      <Box sx={{ textAlign: "right" }}>Price (₹)</Box>
                      <Box sx={{ textAlign: "right" }}>Gross (₹)</Box>
                      <Box sx={{ textAlign: "right" }}>Discount %</Box>
                      <Box sx={{ textAlign: "right" }}>Discount (₹)</Box>
                      <Box sx={{ textAlign: "right" }}>Amount (₹)</Box>
                    </Box>

                    {normalizedItems.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "2.4fr 0.7fr 1fr 1fr 1fr 1fr 1fr",
                          columnGap: 1,
                          borderBottom: "1px solid #eee",
                          padding: "10px 0",
                        }}
                      >
                        <Box>{item.item_name}</Box>
                        <Box sx={{ textAlign: "center" }}>{item.quantity}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.item_price)}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.gross)}</Box>
                        <Box sx={{ textAlign: "right" }}>{formatMoney(item.discount_percent)}%</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.discount_amount)}</Box>
                        <Box sx={{ textAlign: "right" }}>₹{formatMoney(item.subtotal)}</Box>
                      </Box>
                    ))}
                  </div>
                </Grid>

                {/* Terms + Totals */}
                <Grid container style={{ padding: "20px", marginTop: "20px", alignItems: "flex-start", fontSize: "16px", lineHeight: "22px" }}>
                  <Grid item xs={7}>
                    <Typography style={{ fontWeight: "bold", borderBottom: "1px solid #ccc", paddingBottom: "6px" }}>
                      Estimate Terms
                    </Typography>
                    <Typography style={{ paddingTop: "6px" }}>
                      This is an estimate, not a tax invoice or a bill. Please confirm this estimate before {formatDate(estimateData.expiryDate)}; prices may change after this date.
                    </Typography>
                    {estimateData.notes ? (
                      <Typography style={{ paddingTop: "10px" }}>
                        <b>Notes:</b> {estimateData.notes}
                      </Typography>
                    ) : null}
                  </Grid>

                  <Grid item xs={5} style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ textAlign: "right" }}>
                      {summaryRows.map((row, i) => (
                        <Typography
                          key={i}
                          fontWeight={row.bold ? "bold" : "normal"}
                          style={row.big ? { marginTop: "8px", color: "#0e3f66", fontWeight: "bold" } : undefined}
                        >
                          {row.label}
                        </Typography>
                      ))}
                    </div>
                  </Grid>
                </Grid>

                <Grid container style={{ padding: "20px", alignItems: "flex-end" }}>
                  <Grid item xs={8}>
                    <Typography variant="subtitle1">
                      Estimate Amount in Words: <b>{numberToWords(grandTotal)} Rupees Only</b>
                    </Typography>
                  </Grid>
                  <Grid item xs={4} style={{ textAlign: "right" }}>
                    <Typography variant="subtitle1" component="div" fontWeight="bold">
                      Authorised Signatory
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container style={{ padding: "10px 20px", alignItems: "center" }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      Note: This is a digitally generated estimate, not a tax invoice.
                    </Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Paper size / print type */}
              <Grid container justifyContent="center" style={{ marginTop: "24px" }}>
                <Grid item xs={12} sm={8} md={4}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="estimate-preview-paper-size-label">Paper size (PDF and print)</InputLabel>
                    <Select
                      labelId="estimate-preview-paper-size-label"
                      label="Paper size (PDF and print)"
                      value={estimatePaperSize}
                      onChange={(e) => setEstimatePaperSize(normalizeEstimatePageSizeKey(e.target.value))}
                    >
                      <MenuItem value="a4">A4 (210 × 297 mm)</MenuItem>
                      <MenuItem value="a5">A5 (148 × 210 mm)</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" variant="outlined" style={{ marginTop: "16px" }}>
                    <InputLabel id="estimate-preview-print-type-label">Print type</InputLabel>
                    <Select
                      labelId="estimate-preview-print-type-label"
                      label="Print type"
                      value={estimatePrintType}
                      onChange={(e) => setEstimatePrintType(normalizeEstimatePrintType(e.target.value))}
                    >
                      <MenuItem value="LETTERHEAD">Estimate with Letterhead</MenuItem>
                      <MenuItem value="WITHOUT_LETTERHEAD">Estimate without Letterhead</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {/* Buttons */}
              <Grid container justifyContent="center" alignItems="center" spacing={4} style={{ marginTop: "16px" }}>
                <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={handleDownload}
                    style={{
                      width: "100%",
                      padding: "18px 0",
                      backgroundColor: "#21cdc0",
                      color: "#ffffff",
                      borderRadius: "10px",
                      fontSize: "22px",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    DOWNLOAD PDF
                  </button>
                </Grid>
                <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={handlePrint}
                    style={{
                      width: "100%",
                      padding: "18px 0",
                      backgroundColor: "#21cdc0",
                      color: "#ffffff",
                      borderRadius: "10px",
                      fontSize: "22px",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    PRINT
                  </button>
                </Grid>
                <Grid item xs={12} md={4} style={{ textAlign: "center" }}>
                  <button
                    type="button"
                    onClick={handleHomeButton}
                    style={{
                      width: "100%",
                      padding: "18px 0",
                      backgroundColor: "#21cdc0",
                      color: "#fff",
                      borderRadius: "10px",
                      fontSize: "22px",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    GO TO HOME
                  </button>
                </Grid>
              </Grid>
            </>
          ) : (
            <h1>Loading Estimate</h1>
          )}
        </Card>
      </Card>
    </>
  );
};

export default EstimatePreview;
